import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import { Box } from '@mui/material';
import { Table, useModalContext } from '@vxui/components';
import { FC, useCallback, useMemo } from 'react';
import { TableColumn, TableProps } from 'react-data-table-component';
import { Trans, useTranslation } from 'react-i18next';
import { useConfirmationDialog } from '../../hooks';
import { User } from '../../models';
import { hasAdminRole, useDeleteUserMutation, useGetAuthQuery } from '../../services';
import { TableActionButton } from '../table';
import { ResetPasswordModal, resetPasswordModalId } from './ResetPasswordModal';

type UsersTableProps = Partial<TableProps<User>> & { data: TableProps<User>['data'] };

export const UsersTable: FC<UsersTableProps> = props => {
  const { t } = useTranslation();

  const { data: me } = useGetAuthQuery();

  const [deleteUser] = useDeleteUserMutation();
  const { openConfirmationDialog } = useConfirmationDialog<User>({
    modalId: 'deleteUserConfirmation',
    onConfirm: ({ userID }) => deleteUser(userID),
    titleFormatter: () => t('auth.modal.deleteUser.title'),
    messageFormatter: user => <Trans i18nKey="auth.modal.deleteUser.message" values={user} />,
    isDelete: true,
  });

  const { showModal, hideModal } = useModalContext();

  // const closeUpdateUserModal = useCallback(() => hideModal(updateUserModalId), [hideModal]);
  // const openUpdateUserModal = useCallback(
  //   (user: User) => showModal(updateUserModalId, <UpdateUserModal user={user} onClose={closeUpdateUserModal} />),
  //   [closeUpdateUserModal, showModal],
  // );

  const closeResetPasswordModal = useCallback(() => hideModal(resetPasswordModalId), [hideModal]);
  const openResetPasswordModal = useCallback(
    (user: User) =>
      showModal(resetPasswordModalId, <ResetPasswordModal user={user} onClose={closeResetPasswordModal} />),
    [closeResetPasswordModal, showModal],
  );

  const columns = useMemo<TableColumn<User>[]>(() => {
    const isAdmin = hasAdminRole(me);

    return [
      { id: 'username', name: t('auth.columns.username'), selector: ({ username }) => username },
      { id: 'login', name: t('auth.columns.login'), selector: ({ login }) => login },
      { id: 'role', name: t('auth.columns.role'), selector: ({ role }) => role },
      {
        id: 'actions',
        name: '',
        right: true,
        width: '100px',
        omit: !isAdmin,
        cell: user => {
          const isCurrentUser = me?.userID === user.userID;
          return (
            <Box display="flex" gap="8px">
              {/* <TableActionButton
                title={t('button.edit')}
                Icon={EditIcon}
                color="secondary"
                variant="contained"
                size="small"
                onClick={() => openUpdateUserModal(user)}
              /> */}
              <TableActionButton
                title={t(isCurrentUser ? 'button.resetPasswordDisabled' : 'button.resetPassword')}
                Icon={LockResetIcon}
                color="tertiary"
                variant="contained"
                size="small"
                onClick={() => openResetPasswordModal(user)}
                disabled={isCurrentUser}
              />
              <TableActionButton
                title={t('button.delete')}
                Icon={DeleteIcon}
                color="error"
                variant="contained"
                size="small"
                onClick={() => openConfirmationDialog(user)}
              />
            </Box>
          );
        },
      },
    ];
  }, [openResetPasswordModal, openConfirmationDialog, me, t]);

  return (
    <Table {...props} id="users" columns={columns} dense fixedHeader fixedHeaderScrollHeight="calc(100vh - 220px)" />
  );
};
